import { SEGMENT_NAME_MAX, SegmentDetailSchema, SegmentPersonSchema, SegmentRuleSchema, EmployeeFieldSchema, EmployeeFieldValueSchema, RulePersonSchema, SegmentMemberSchema, SegmentSchema, MatchTypes } from "#shared/segmentsTypes.ts";
import { oc } from "@orpc/contract";
import { z } from "zod";

const SegmentDefinitionSchema = z.object({
    name: z.string().trim().min(1).max(SEGMENT_NAME_MAX),
    isRuleBased: z.boolean(),
    matchType: z.enum(MatchTypes),
    rules: z.array(SegmentRuleSchema).optional(),
    personIds: z.array(z.string()).optional(),
    tags: z.array(z.string())
});

const GetSegmentInputSchema = z.object({
    orgId: z.string(),
    segmentId: z.string()
});

const SegmentIdInputSchema = z.object({
    segmentId: z.string()
});

const CreateSegmentOutputSchema = z.object({
    id: z.string()
});

const ListEmployeeFieldsInputSchema = z.object({
    orgId: z.string()
});

const ListEmployeeFieldsOutputSchema = z.object({
    items: z.array(EmployeeFieldSchema)
});

const ListEmployeeFieldValuesInputSchema = z.object({
    orgId: z.string(),
    fieldId: z.string()
});

const ListEmployeeFieldValuesOutputSchema = z.object({
    items: z.array(EmployeeFieldValueSchema)
});

const SearchRulePeopleInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional(),
    ids: z.array(z.string()).optional()
});

const SearchRulePeopleOutputSchema = z.object({
    items: z.array(RulePersonSchema)
});

const PreviewSegmentMembersInputSchema = z.object({
    orgId: z.string(),
    matchType: z.enum(MatchTypes),
    rules: z.array(SegmentRuleSchema),
    continuationToken: z.string().optional(),
    pageSize: z.number().default(50)
});

const PreviewSegmentMembersOutputSchema = z.object({
    items: z.array(SegmentMemberSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

const SearchPeopleInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional(),
    continuationToken: z.string().optional()
});

const SearchPeopleOutputSchema = z.object({
    items: z.array(SegmentPersonSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

const ListVisibleSegmentsInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional(),
    continuationToken: z.string().optional(),
    pageSize: z.number().default(25)
});

const ListVisibleSegmentsOutputSchema = z.object({
    items: z.array(SegmentSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number()
});

const SearchSegmentTagsInputSchema = z.object({
    orgId: z.string(),
    q: z.string().optional()
});

const SearchSegmentTagsOutputSchema = z.object({
    items: z.array(z.string())
});

const segmentErrors = {
    NOT_FOUND: { message: "Segment not found", data: SegmentIdInputSchema }
} as const;

export const segmentsContract = {
    getSegment: oc.route({ method: "GET", path: "/get-segment" }).errors(segmentErrors).input(GetSegmentInputSchema).output(SegmentDetailSchema),
    createSegment: oc.route({ method: "POST", path: "/create-segment" }).input(SegmentDefinitionSchema).output(CreateSegmentOutputSchema),
    updateSegment: oc.route({ method: "POST", path: "/update-segment" }).errors(segmentErrors).input(SegmentDefinitionSchema.extend(SegmentIdInputSchema.shape)).output(z.void()),
    removeSegment: oc.route({ method: "POST", path: "/remove-segment" }).errors(segmentErrors).input(SegmentIdInputSchema).output(z.void()),
    listEmployeeFields: oc.route({ method: "GET", path: "/list-employee-fields" }).input(ListEmployeeFieldsInputSchema).output(ListEmployeeFieldsOutputSchema),
    listEmployeeFieldValues: oc.route({ method: "GET", path: "/list-employee-field-values" }).input(ListEmployeeFieldValuesInputSchema).output(ListEmployeeFieldValuesOutputSchema),
    searchRulePeople: oc.route({ method: "GET", path: "/search-rule-people" }).input(SearchRulePeopleInputSchema).output(SearchRulePeopleOutputSchema),
    searchPeople: oc.route({ method: "GET", path: "/search-people" }).input(SearchPeopleInputSchema).output(SearchPeopleOutputSchema),
    searchSegmentTags: oc.route({ method: "GET", path: "/search-segment-tags" }).input(SearchSegmentTagsInputSchema).output(SearchSegmentTagsOutputSchema),
    previewSegmentMembers: oc.route({ method: "GET", path: "/preview-segment-members" }).input(PreviewSegmentMembersInputSchema).output(PreviewSegmentMembersOutputSchema),
    listVisibleSegments: oc.route({ method: "GET", path: "/list-visible-segments" }).input(ListVisibleSegmentsInputSchema).output(ListVisibleSegmentsOutputSchema),
    starSegment: oc.route({ method: "POST", path: "/star-segment" }).errors(segmentErrors).input(SegmentIdInputSchema).output(z.void()),
    unstarSegment: oc.route({ method: "POST", path: "/unstar-segment" }).errors(segmentErrors).input(SegmentIdInputSchema).output(z.void())
};
